import { useState } from "react";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import { Alert, Button, Stack, Typography } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link as RouterLink, useParams } from "react-router-dom";

import {
  CustomerRoleSchema,
  DIRECTORY_PAGE_LIMIT_DEFAULT,
  GrantMembershipRequestSchema,
  sessionContextKindOf,
} from "@thalamos/haven-common";
import type {
  CustomerRole,
  Organisation,
  OrganisationId,
  Role,
  UpdateUserProfileRequest,
  User,
  UserId,
  UserMembership,
} from "@thalamos/haven-common";

import api, { apiHooks } from "../../api/index.js";
import type { OrganisationSearch } from "../../components/OrganisationPicker/OrganisationPicker.js";
import {
  PasswordResetDialog,
  type PasswordResetOutcome,
} from "../../components/PasswordResetDialog/PasswordResetDialog.js";
import type { MembershipChangeOutcome } from "../../components/UserMembershipsPanel/UserMembershipsPanel.js";
import { UserMembershipsPanel } from "../../components/UserMembershipsPanel/UserMembershipsPanel.js";
import type { ProfileSaveOutcome } from "../../components/UserProfileForm/UserProfileForm.js";
import { UserProfileForm } from "../../components/UserProfileForm/UserProfileForm.js";
import { useUser } from "../../Context/UserContext.js";
import { createRoute } from "../../routing/createRoute.js";
import { useDirectoryQuery } from "./useDirectoryQuery.js";

/**
 * An organisation's admin, granted in a section of its own apart from case work.
 * Thalamos's own administrative membership is not one: it stays with the other
 * memberships, shown as set by the directory group.
 */
const isOrganisationAdmin = (role: Role): boolean =>
  sessionContextKindOf(role) === "organisation_administration";

/**
 * Read from the schema, so a role the endpoint admits is offered somewhere, in
 * the schema's order; nothing about the domain says one comes first, and the
 * first is preselected.
 */
const CASE_WORK_ROLES = CustomerRoleSchema.options.filter(
  (role) => !isOrganisationAdmin(role),
);
const ADMIN_ROLES = CustomerRoleSchema.options.filter(isOrganisationAdmin);

/**
 * Why an account cannot be shown.
 *
 * `not_found` is a 404, the server's answer both for an id nobody holds and for
 * an account this session may not see — indistinguishable on purpose. Everything
 * else is `unavailable`, and has to be said differently: telling an
 * administrator that an account does not exist because the network dropped is
 * telling them something untrue about the data.
 */
export type AccountLoadFailure = "not_found" | "unavailable";

/**
 * Why the account could not be shown, or `undefined` while it still might be.
 *
 * Read off the status rather than the error, because the shared axios instance
 * resolves 4xx and only rejects from 5xx up — so a refusal arrives as a
 * *successful* request carrying an error body.
 */
export const accountLoadFailure = (
  status: number | undefined,
  errored: boolean,
): AccountLoadFailure | undefined => {
  if (status === 200) {
    return undefined;
  }

  if (status === 404) {
    return "not_found";
  }

  // No status and no error means the request is still in flight.
  return errored || status !== undefined ? "unavailable" : undefined;
};

/** The account the address names and every change that can be made to it, shaped as `AdminUserPageContent`'s props. */
export const useManagedAccount = (requestedId: string) => {
  const { user: signedIn } = useUser();
  const [{ data, loading, error, response }, refetch] =
    apiHooks.user.byId(requestedId);

  const [
    {
      data: memberships,
      loading: membershipsLoading,
      error: membershipsError,
      response: membershipsResponse,
    },
    refetchMemberships,
  ] = apiHooks.user.memberships(requestedId);

  // A 4xx body resolves onto `data`, so an account is only ever taken from a
  // 200; otherwise `{ message: "Not found" }` would reach the form as though
  // it were one.
  const account = response?.status === 200 ? data : undefined;

  // Changes go to the account the server returned, never to the id in the
  // address.
  const userIdToChange = (): UserId => {
    // Unreachable: the form, password reset and membership panels render only
    // while the account is read, and a failed re-read takes them off screen.
    // Kept to fail loudly if not.
    /* v8 ignore if */
    if (account === undefined) {
      throw new Error("Expected a change only once the account is read");
    }

    return account.id;
  };

  const onSave = async (
    userIdToSave: UserId,
    request: UpdateUserProfileRequest,
  ): Promise<ProfileSaveOutcome> => {
    const response = await api.user.updateProfile(userIdToSave, request);

    if (response.status >= 400) {
      return "failed";
    }

    // Re-read rather than trusting what was sent, so the page shows the account
    // as it now stands — including anything the server settled, such as the
    // name it trimmed.
    await refetch();
    return "saved";
  };

  /**
   * Applies a change and refreshes the list through the same read that populated
   * it, so the page has one source for what the account holds rather than two
   * that can disagree.
   */
  const applyChange = async (
    request: Promise<{ status: number }>,
  ): Promise<MembershipChangeOutcome> => {
    const response = await request;

    if (response.status >= 400) {
      return "failed";
    }

    await refetchMemberships();
    return "changed";
  };

  const onSendPasswordReset = async (): Promise<PasswordResetOutcome> => {
    const response = await api.user.sendPasswordReset(userIdToChange());

    if (response.status === 204) {
      return "sent";
    }

    // The account was read, so its id is well formed and a 400 is the refusal
    // `config.user.sendPasswordReset` documents.
    return response.status === 400 ? "signs_in_through_organisation" : "failed";
  };

  return {
    user: account,
    loading,
    failure: accountLoadFailure(
      response?.status,
      error !== undefined && error !== null,
    ),
    onSave,
    memberships:
      membershipsResponse?.status === 200
        ? memberships?.memberships
        : undefined,
    membershipsLoading,
    membershipsUnavailable:
      accountLoadFailure(
        membershipsResponse?.status,
        membershipsError !== undefined && membershipsError !== null,
      ) !== undefined,
    onGrant: (organisationId: OrganisationId, role: CustomerRole) =>
      applyChange(
        api.user.grantMembership(
          userIdToChange(),
          GrantMembershipRequestSchema.parse({ organisationId, role }),
        ),
      ),
    onRevoke: (organisationId: OrganisationId, role: CustomerRole) =>
      applyChange(
        api.user.revokeMembership(userIdToChange(), organisationId, role),
      ),
    onSendPasswordReset,
    isOwnAccount: account !== undefined && signedIn?.id === account.id,
  };
};

/** One account, as Thalamos staff maintaining it see it. */
export const AdminUserPage = () => {
  const { userId } = useParams();

  // `useParams` types every segment as optional, but the route cannot match
  // without this one. The fallback is there to satisfy that type rather than to
  // describe a state the page can be in.
  const account = useManagedAccount(userId ?? "");

  // The picker's options, narrowed by what has been typed into it. The same
  // debounce the directory listings use, because the request is the same
  // expensive one — a search across every organisation.
  const { query, input, setQuery } = useDirectoryQuery();
  const [{ data: organisations }] = apiHooks.admin.organisations({
    query,
    limit: DIRECTORY_PAGE_LIMIT_DEFAULT,
  });
  // A second picker for the admin section, so typing in one does not move the
  // other's results. It asks only while its search differs from the first
  // one's: the same search has the same answer, and the request is expensive.
  const adminQuery = useDirectoryQuery();
  const adminSearchesAlike = adminQuery.query === query;
  const [{ data: adminOrganisations }] = apiHooks.admin.organisations(
    { query: adminQuery.query, limit: DIRECTORY_PAGE_LIMIT_DEFAULT },
    { manual: adminSearchesAlike },
  );

  return (
    <AdminUserPageContent
      {...account}
      backTo={createRoute.adminUsers()}
      organisationPath={(organisationId) =>
        createRoute.adminOrganisation(organisationId)
      }
      organisations={organisations?.results ?? []}
      organisationSearch={{ input, onInput: setQuery }}
      adminOrganisations={
        (adminSearchesAlike ? organisations : adminOrganisations)?.results ?? []
      }
      adminOrganisationSearch={{
        input: adminQuery.input,
        onInput: adminQuery.setQuery,
      }}
    />
  );
};

/**
 * The page's rendering, with the account and every change to it injected. Mounting it
 * needs no API client, so a story or a test can show any state it has.
 *
 * Loading is told apart from both failures deliberately: an administrator who
 * reads "no such account" while the request is still in flight has been told
 * something untrue.
 */
export const AdminUserPageContent = ({
  user,
  loading,
  failure,
  onSave,
  memberships,
  membershipsLoading,
  membershipsUnavailable,
  backTo,
  organisationPath,
  organisations,
  organisationSearch,
  adminOrganisations,
  adminOrganisationSearch,
  onGrant,
  onRevoke,
  onSendPasswordReset,
  isOwnAccount,
}: {
  user: User | undefined;
  loading: boolean;
  failure: AccountLoadFailure | undefined;
  /**
   * Takes the account's id as well as the values, so the change is saved against
   * the account that was read rather than against the path segment it was asked
   * for — and the save carries no branch for there being no account yet.
   */
  onSave: (
    userId: UserId,
    request: UpdateUserProfileRequest,
  ) => Promise<ProfileSaveOutcome>;
  /**
   * Absent while the set has not been read. Distinct from an empty array, which
   * is an account that genuinely holds nothing — a state the panel says out
   * loud, because it means the person can do nothing at all yet.
   */
  memberships: readonly UserMembership[] | undefined;
  membershipsLoading: boolean;
  membershipsUnavailable: boolean;
  /** The listing the back link returns to. */
  backTo: string;
  /** Where an organisation's name links to; without one, it is plain text. */
  organisationPath?: ((organisationId: OrganisationId) => string) | undefined;
  organisations: readonly Organisation[];
  organisationSearch?: OrganisationSearch | undefined;
  adminOrganisations: readonly Organisation[];
  adminOrganisationSearch?: OrganisationSearch | undefined;
  onGrant: (
    organisationId: OrganisationId,
    role: CustomerRole,
  ) => Promise<MembershipChangeOutcome>;
  onRevoke: (
    organisationId: OrganisationId,
    role: CustomerRole,
  ) => Promise<MembershipChangeOutcome>;
  onSendPasswordReset: () => Promise<PasswordResetOutcome>;
  /** The account is the signed-in person's, whose own Admin role is not theirs to remove. */
  isOwnAccount: boolean;
}) => {
  const { t } = useTranslation();
  const [resetting, setResetting] = useState(false);
  const [resetSent, setResetSent] = useState(false);

  return (
    <Stack spacing={3} sx={{ maxWidth: 640 }}>
      <Button
        component={RouterLink}
        to={backTo}
        startIcon={<ArrowBackIcon />}
        size="small"
        sx={{ alignSelf: "flex-start" }}
      >
        {t("admin.users.backToList")}
      </Button>

      <Typography variant="h5" component="h1">
        {user === undefined ? t("admin.users.account") : user.name}
      </Typography>

      {loading ? (
        <Typography variant="body2" color="text.secondary">
          {t("common.loading")}
        </Typography>
      ) : user !== undefined ? (
        <>
          <UserProfileForm
            user={user}
            onSave={(request) => onSave(user.id, request)}
          />

          <Stack spacing={2} sx={{ alignItems: "flex-start" }}>
            <Button
              variant="outlined"
              onClick={() => {
                setResetSent(false);
                setResetting(true);
              }}
            >
              {t("admin.users.passwordReset.open")}
            </Button>
            {resetSent ? (
              <Alert severity="success">
                {t("admin.users.passwordReset.sent", { name: user.name })}
              </Alert>
            ) : null}
          </Stack>
          <PasswordResetDialog
            open={resetting}
            name={user.name}
            onClose={() => {
              setResetting(false);
            }}
            onSend={async () => {
              const outcome = await onSendPasswordReset();
              setResetSent(outcome === "sent");
              return outcome;
            }}
          />

          {/* Only alongside an account that was read. Offering to grant a
              membership beside "that account does not exist" would be offering to
              act on nothing. */}
          <UserMembershipsPanel
            section="case-work"
            grantable={CASE_WORK_ROLES}
            memberships={(memberships ?? []).filter(
              (membership) => !isOrganisationAdmin(membership.role),
            )}
            loading={membershipsLoading}
            unavailable={membershipsUnavailable}
            organisations={organisations}
            organisationSearch={organisationSearch}
            organisationPath={organisationPath}
            onGrant={onGrant}
            onRevoke={onRevoke}
          />
          <UserMembershipsPanel
            section="admin"
            grantable={ADMIN_ROLES}
            memberships={(memberships ?? []).filter((membership) =>
              isOrganisationAdmin(membership.role),
            )}
            loading={membershipsLoading}
            unavailable={membershipsUnavailable}
            organisations={adminOrganisations}
            organisationSearch={adminOrganisationSearch}
            organisationPath={organisationPath}
            onGrant={onGrant}
            onRevoke={isOwnAccount ? undefined : onRevoke}
          />
        </>
      ) : (
        <Typography variant="body2" color="error">
          {failure === "not_found"
            ? t("admin.users.notFound")
            : t("admin.users.loadFailed")}
        </Typography>
      )}
    </Stack>
  );
};
